import * as React from "react";
import * as _ from "underscore";
import { Button, TextInput, Modal, Select, SelectItem } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { AppShape, HurdleShape, DocTypes } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";
import DocDisplay from "web_modules/underwriter_dashboard/shared/DocDisplay";
import FieldEntry from "web_modules/underwriter_dashboard/shared/FieldEntry";
import { loadApp } from "web_modules/underwriter_dashboard/actions";
import { selectableCOECategories } from "underwriter_dashboard/shared";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`../../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/purchase_contract.scss`);

const pcLayout = [
  {
    sectionName: "Seller(s)",
    fields: [
      {
        fieldKey: "seller1Name",
        fieldType: "name",
        fieldName: "Seller 1's name",
      },
      {
        fieldKey: "seller2Name",
        fieldType: "name",
        fieldName: "Seller 2's name (if any)",
      },
    ],
  },
  {
    sectionName: "Borrower(s)",
    sectionDesc: "<p>Must not contain 'assigns' or 'nominee'</p>",
    fields: [
      {
        fieldKey: "borrower1Name",
        fieldType: "name",
        fieldName: "Borrower 1's name",
      },
      {
        fieldKey: "borrower2Name",
        fieldType: "name",
        fieldName: "Borrower 2's name (if any)",
      },
    ],
  },
  {
    sectionName: "Property",
    fields: [
      {
        fieldKey: "propertyAddress",
        fieldType: "address",
        fieldName: "Address",
      },
      {
        fieldKey: "propertyCounty",
        fieldType: "string",
        fieldName: "County",
      },
      {
        fieldKey: "propertyLot",
        fieldType: "string",
        fieldName: "Lot",
      },
      {
        fieldKey: "propertyBlock",
        fieldType: "string",
        fieldName: "Block",
      },
      {
        fieldKey: "propertyApn",
        fieldType: "string",
        fieldName: "APN",
      },
    ],
  },
  {
    sectionName: "Sales price",
    fields: [
      {
        fieldKey: "salesPriceCashPortion",
        fieldType: "money",
        fieldName: "Cash portion",
      },
      {
        fieldKey: "salesPriceFinancedPortion",
        fieldType: "money",
        fieldName: "Financed portion",
      },
      {
        fieldKey: "salesPriceTotalAmount",
        fieldType: "money",
        fieldName: "Total amount",
      },
    ],
  },
  {
    sectionName: "Seller financing",
    fields: [
      {
        fieldKey: "sellerFinancingAmount",
        fieldType: "money",
        fieldName: "Amount",
      },
    ],
  },
  {
    sectionName: "License holder disclosure",
    fields: [
      {
        fieldKey: "licenseHolderDisclosure",
        fieldType: "string",
        fieldName: "Disclosure",
      },
    ],
  },
  {
    sectionName: "Earnest money",
    fields: [
      {
        fieldKey: "earnestMoneyInitialAmount",
        fieldType: "money",
        fieldName: "Initial amount",
      },
      {
        fieldKey: "earnestMoneyAdditionalAmount",
        fieldType: "money",
        fieldName: "Additional amount",
      },
      {
        fieldKey: "earnestMoneyDaysUntilAdditional",
        fieldType: "integer",
        fieldName: "Days until additional",
      },
      {
        fieldKey: "escrowCompanyName",
        fieldType: "string",
        fieldName: "Escrow company name",
      },
      {
        fieldKey: "escrowCompanyAddress",
        fieldType: "address",
        fieldName: "Escrow company address",
      },
    ],
  },
  {
    sectionName: "Title policy & survey",
    fields: [
      {
        fieldKey: "titleInsuranceCompanyName",
        fieldType: "string",
        fieldName: "Title insurance company name",
      },
      {
        fieldKey: "titleDaysUntilSellerFurnishesExisting",
        fieldType: "integer",
        fieldName: "Days until seller furnishes existing",
      },
      {
        fieldKey: "titleDaysUntilBorrowerFurnishesExisting",
        fieldType: "integer",
        fieldName: "Days until borrower furnishes existing",
      },
      {
        fieldKey: "titleDaysUntilSellerObtainsNew",
        fieldType: "integer",
        fieldName: "Days until seller obtains new",
      },
    ],
  },
  {
    sectionName: "Property condition",
    fields: [
      {
        fieldKey: "propertyConditionRepairsAndTreatments",
        fieldType: "string",
        fieldName: "Repairs & treatments",
      },
      {
        fieldKey: "propertyConditionCostOfRepairs",
        fieldType: "money",
        fieldName: "Cost of repairs",
      },
    ],
  },
  {
    sectionName: "Closing",
    fields: [
      {
        fieldKey: "closingDate",
        fieldType: "date",
        fieldName: "COE Date",
      },
    ],
  },
  {
    sectionName: "Agreement of parties",
    fields: [
      {
        fieldKey: "listOfAddenda",
        fieldType: "string",
        fieldName: "List of addenda",
      },
    ],
  },
  {
    sectionName: "Attorneys",
    fields: [
      {
        fieldKey: "sellerAttorneyName",
        fieldType: "name",
        fieldName: "Attorney for seller(s)",
      },
      {
        fieldKey: "borrowerAttorneyName",
        fieldType: "name",
        fieldName: "Attorney for borrower(s)",
      },
    ],
  },
  {
    sectionName: "Agent for seller(s)",
    fields: [
      {
        fieldKey: "sellerAgentName",
        fieldType: "name",
        fieldName: "Agent's name",
      },
      {
        fieldKey: "sellerAgentPhone",
        fieldType: "string",
        fieldName: "Agent's phone number",
      },
      {
        fieldKey: "sellerAgentEmail",
        fieldType: "string",
        fieldName: "Agent's email address",
      },
      {
        fieldKey: "sellerAgentNmlsId",
        fieldType: "string",
        fieldName: "Agent's NMLS ID",
      },
      {
        fieldKey: "sellerAgentCompanyName",
        fieldType: "string",
        fieldName: "Company name",
      },
      {
        fieldKey: "sellerAgentCompanyAddress",
        fieldType: "address",
        fieldName: "Company address",
      },
    ],
  },
  {
    sectionName: "Agent for borrower(s)",
    fields: [
      {
        fieldKey: "borrowerAgentName",
        fieldType: "name",
        fieldName: "Agent's name",
      },
      {
        fieldKey: "borrowerAgentPhone",
        fieldType: "string",
        fieldName: "Agent's phone number",
      },
      {
        fieldKey: "borrowerAgentEmail",
        fieldType: "string",
        fieldName: "Agent's email address",
      },
      {
        fieldKey: "borrowerAgentNmlsId",
        fieldType: "string",
        fieldName: "Agent's NMLS ID",
      },
      {
        fieldKey: "borrowerAgentCompanyName",
        fieldType: "string",
        fieldName: "Company name",
      },
      {
        fieldKey: "borrowerAgentCompanyAddress",
        fieldType: "address",
        fieldName: "Company address",
      },
    ],
  },
  {
    sectionName: "Offer negotiation",
    fields: [
      {
        fieldKey: "offerCountered",
        fieldType: "boolean",
        fieldName: "Countered?",
      },
      {
        fieldKey: "offerRejected",
        fieldType: "boolean",
        fieldName: "Rejected?",
      },
    ],
  },
  {
    sectionName: "Signatures",
    fields: [
      {
        fieldKey: "sellerSignatureDate",
        fieldType: "date",
        fieldName: "Seller(s) signature date",
      },
      {
        fieldKey: "borrowerSignatureDate",
        fieldType: "date",
        fieldName: "Borrower(s) signature date",
      },
    ],
  },
  {
    sectionName: "Personal property & concessions",
    sectionDesc: `<p>TODO: link to table of values</p>
<p>Include any personal property in seller concessions amount below</p>`,
    fields: [
      {
        fieldKey: "personalPropertyIncluded",
        fieldType: "string",
        fieldName: "Personal property included",
      },
      {
        fieldKey: "sellerConcessions", // TODO: Rename to sellerCreditDollars
        fieldType: "money",
        fieldName: "Seller Credit Dollars",
      },
      {
        fieldKey: "sellerCreditPercent",
        fieldType: "decimal",
        fieldName: "Seller Credit Percent",
      },
    ],
  },
  {
    sectionName: "Other checks",
    fields: [
      {
        fieldKey: "evidenceOfThirdPartyFinancing",
        fieldType: "boolean",
        fieldName: "Evidence of third-party financing?",
      },
      {
        fieldKey: "entityListedAsSeller",
        fieldType: "boolean",
        fieldName: "Any entity listed as seller?",
      },
    ],
  },
];

interface EditorProps {
  dispatch: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class PCEditor extends React.Component<EditorProps, any> {
  constructor(props) {
    super(props);
    this.state = PCEditor.initialState(props);
  }

  componentWillReceiveProps(nextProps) {
    this.setState(PCEditor.initialState(nextProps));
  }

  static getFieldKeysFromSections(sections) {
    return _.flatten(
      _.map(sections, (section: any) => _.map(section.fields, (field: any) => field.fieldKey)),
    );
  }

  static initialState(props) {
    const { purchase_contract } = props.app;

    // if PC exists, we can copy k-v pairs directly from there; otherwise,
    // re-create structure (with all null values) using section-field layout

    const keysToExclude = ["createdAt", "updatedAt"];

    const values = {};
    if (purchase_contract) {
      _.each(purchase_contract, (value, key: string) => {
        if (!_.contains(keysToExclude, key)) {
          values[key] = value;
        }
      });
    } else {
      _.each(PCEditor.getFieldKeysFromSections(pcLayout), key => {
        values[key] = null;
      });
    }

    return {
      values,
      editsMade: false,
      showCOEReasonModal: false,
      COEDateChangeReason: "",
      COEDateChangeCategory: "",
    };
  }

  createMessage(message) {
    const { app, hurdle } = this.props;

    APIClient.post("/messages", {
      message,
      application_id: app.id,
      hurdle_name: hurdle.name,
    });
  }

  createFlag(message) {
    const { app, hurdle } = this.props;

    APIClient.post("/flags", {
      message,
      application_id: app.id,
      hurdle_name: hurdle.name,
    });
  }

  setShowCOEReasonModal(showModal: boolean) {
    if (!showModal) {
      this.setState({
        showCOEReasonModal: showModal,
        COEDateChangeReason: "",
        COEDateChangeCategory: "",
      });
    } else {
      this.setState({
        showCOEReasonModal: showModal,
      });
    }
  }

  handleFormChange(k: string, v: string) {
    this.setState({
      [k]: v,
    });
  }

  upsertValue(key, value) {
    const { values } = this.state;
    this.setState({
      values: {
        ...values,
        [key]: value,
      },
      editsMade: true,
    });
  }

  deleteValue(key) {
    const { values } = this.state;
    this.setState({
      values: {
        ...values,
        [key]: null,
      },
      editsMade: true,
    });
  }

  syncValues() {
    const { app } = this.props;
    const { COEDateChangeReason, COEDateChangeCategory, values } = this.state;
    const payload = {
      purchase_contract: values,
      closing_date_change_reason: COEDateChangeReason,
      closing_date_change_category: COEDateChangeCategory,
    };

    if (app.purchase_contract) {
      return APIClient.put(`/loan_applications/${app.id}/newdoc/purchase_contract`, payload);
    }

    return APIClient.post(`/loan_applications/${app.id}/newdoc/purchase_contract`, payload);
  }

  onSave() {
    const { dispatch, app } = this.props;
    const { COEDateChangeReason, COEDateChangeCategory, values } = this.state;
    const { sellerConcessions, closingDate } = values;
    const sellerCreditPercent = parseFloat(values.sellerCreditPercent);

    // TODO: Make this validation more canonical
    // Validate that at least one of seller concession or seller credit percent is null
    if (sellerCreditPercent && sellerConcessions) {
      alert("Seller Credit can only be specified as a dollar amount or as percentage");
      return;
    }
    if (sellerCreditPercent && (sellerCreditPercent < 1 || sellerCreditPercent > 100)) {
      alert("The percent should be a whole number between 1 and 100");
      return;
    }

    if (
      COEDateChangeReason === "" &&
      COEDateChangeCategory === "" &&
      closingDate !== app.purchased_property_closing_date &&
      app.target_closing_date
    ) {
      // COE Date has changed from the Target Date
      this.setShowCOEReasonModal(true);
      return;
    }

    this.syncValues()
      .then(() => {
        loadApp(dispatch, app.id);
      })
      .catch(() => {
        alert(
          `there was an error saving the changes, please contact engineering or reload the page and try again`,
        );
        throw new Error(
          `there was an error saving the purchase contract changes to loan application Id: ${app.id}.`,
        );
      });
  }

  getConditionalContent() {
    const requestPCAddendum = v => {
      if (!v) return null;

      const onClick = () => {
        const msg = prompt(
          "Ask borrower to submit PC addendum showing counter-offer",
          "Could you please submit a PC addendum showing the counter-offer?",
        );

        if (msg !== null) {
          this.createMessage(msg);
        }
      };

      return (
        <div className="doc-entry__field">
          <div className="doc-entry__section-desc">
            <p>Is a PC addendum with counter-offer available?</p>
          </div>
          <div className="doc-entry__field-input">
            <Button bsSize="small" onClick={onClick}>
              Request PC addendum
            </Button>
          </div>
        </div>
      );
    };

    const requestNewPC = v => {
      if (!v) return null;

      const onClick = () => {
        const msg = prompt(
          "Ask borrower to submit revised PC",
          "Please change/insert field on page #",
        );

        if (msg !== null) {
          this.createMessage(msg);
        }
      };

      return (
        <div className="doc-entry__field">
          <div className="doc-entry__field-input">
            <Button bsSize="small" onClick={onClick}>
              Request revised purchase contract
            </Button>
          </div>
        </div>
      );
    };

    const requestThirdPartyExplanation = v => {
      if (!v) return null;

      const onClick = () => {
        const msg = prompt(
          "Ask borrower to submit documentation about financing",
          "Could you please provide more documentation about this financing?",
        );

        if (msg !== null) {
          this.createMessage(msg);
        }
      };

      return (
        <div className="doc-entry__field">
          <div className="doc-entry__field-input">
            <Button bsSize="small" onClick={onClick}>
              Request explanation
            </Button>
          </div>
        </div>
      );
    };

    const requestEntityAsSellerDocs = v => {
      if (!v) return null;

      const onClick = () => {
        const msg = prompt(
          "Ask borrower to submit documentation proving seller's signing authority",
          "Could you please provide more documentation proving the seller's signing authority?",
        );

        if (msg !== null) {
          this.createMessage(msg);
          this.createFlag("Require signing authority for seller");
        }
      };

      return (
        <div className="doc-entry__field">
          <div className="doc-entry__field-input">
            <Button bsSize="small" onClick={onClick}>
              Request documentation
            </Button>
          </div>
        </div>
      );
    };

    return {
      offerCountered: requestPCAddendum,
      offerRejected: requestNewPC,
      evidenceOfThirdPartyFinancing: requestThirdPartyExplanation,
      entityListedAsSeller: requestEntityAsSellerDocs,
    };
  }

  render() {
    const { app, onHide } = this.props;
    const {
      editsMade,
      showCOEReasonModal,
      COEDateChangeReason,
      COEDateChangeCategory,
      values,
    } = this.state;

    const pcDocs = _.filter(app.documents, d => d.type === DocTypes.PurchaseContract);

    let content;
    if (pcDocs.length > 0) {
      content = (
        <div className="purchase-contract__container">
          <div className="purchase-contract__pdf">
            <DocDisplay docs={pcDocs} />
          </div>
          <div className="purchase-contract__sidebar">
            <FieldEntry
              layout={pcLayout}
              values={values}
              conditionalContent={this.getConditionalContent()}
              upsertValue={(key, value) => this.upsertValue(key, value)}
              deleteValue={key => this.deleteValue(key)}
            />
          </div>
          <>
            <Modal
              open={showCOEReasonModal}
              modalHeading="Close of Escrow Date Change Reason"
              primaryButtonText="Save"
              secondaryButtonText="Cancel"
              primaryButtonDisabled={COEDateChangeReason === "" || COEDateChangeCategory === ""}
              onBlur={() => this.setShowCOEReasonModal(false)}
              onRequestClose={() => this.setShowCOEReasonModal(false)}
              onRequestSubmit={() => this.onSave()}
            >
              <Select
                light
                id="coe-date-change-category-select"
                name="coe-date-change-category-select"
                labelText="Category"
                placeholder="Select Category"
                value={COEDateChangeCategory}
                onChange={e => {
                  this.handleFormChange("COEDateChangeCategory", e.target.value);
                }}
              >
                <SelectItem key="no-category" text="Select a category" value="" />
                {Object.values(selectableCOECategories).map(c => (
                  <SelectItem key={c.value} text={c.label} value={c.value} />
                ))}
              </Select>
              <TextInput
                id="coe-date-change-reason-text-input"
                value={COEDateChangeReason}
                labelText="Please enter the reason for changing the Close of Escrow Date"
                onChange={e => this.handleFormChange("COEDateChangeReason", e.target.value)}
              />
            </Modal>
          </>
        </div>
      );
    } else {
      content = <p>Waiting for borrower(s) to upload their purchase contract</p>;
    }

    let footer;
    if (editsMade) {
      footer = (
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button onClick={() => this.onSave()} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      );
    } else {
      footer = <></>;
    }

    return (
      <>
        {content}
        {footer}
      </>
    );
  }
}

export default PCEditor;
